import { createSelector } from '@reduxjs/toolkit';
import {
  ADD_UPDATE_FAERBUNG_REVIEW_META,
  ADD_UPDATE_FAERBUNG_REVIEW_SCORE,
  DELETE_FAERBUNG_REVIEW_META,
  FaerbungReivewActionTypes,
  FaerbungReviewMeta,
  FaerbungReviewScore,
  FaerbungReviewState,
  RESET_FAERBUNG_REVIEW_META,
  RootState,
  SET_FAERBUNG_REVIEW_METAS,
  SET_FAERBUNG_REVIEW_SCORE,
} from '../types';

const initialState: FaerbungReviewState = {
  faerbungReviewMeta: {},
  faerbungReviewScore: {
    byId: {},
    allIds: [],
  },
};

function setFaerbungReviewMetas(
  state: FaerbungReviewState, fm: FaerbungReviewMeta[],
): FaerbungReviewState {
  return {
    ...state,
    faerbungReviewMeta: {
      ...state.faerbungReviewMeta,
      byId: fm.reduce((acc, current) => ({
        ...acc,
        [current.id]: current,
      }), {} as Record<number, FaerbungReviewMeta>),
      allIds: fm.map((s) => s.id),
    },
  };
}

function addUpdateFaerbungReviewMetas(
  state: FaerbungReviewState, fm: FaerbungReviewMeta[],
): FaerbungReviewState {
  const byId = fm.reduce((acc, cur) => {
    acc[cur.id] = cur;
    return acc;
  }, { ...state.faerbungReviewMeta.byId });
  const allIds = Object.keys(byId).map((k) => Number(k));
  return {
    ...state,
    faerbungReviewMeta: {
      ...state.faerbungReviewMeta,
      byId,
      allIds,
    },
  };
}

function deleteFaerbungReviewMeta(state: FaerbungReviewState, id: number): FaerbungReviewState {
  if (!state.faerbungReviewMeta.byId) {
    return state;
  }
  const { [id]: del, ...rest } = state.faerbungReviewMeta.byId;
  return {
    ...state,
    faerbungReviewMeta: {
      ...state.faerbungReviewMeta,
      allIds: state.faerbungReviewMeta.allIds?.filter((fid) => fid !== id),
      byId: rest,
    },
  };
}

function setFaerbungReviewScores(
  state: FaerbungReviewState, fs: FaerbungReviewScore[],
): FaerbungReviewState {
  return {
    ...state,
    faerbungReviewScore: {
      ...state.faerbungReviewScore,
      byId: fs.reduce((acc, current) => ({
        ...acc,
        [current.id]: current,
      }), {} as Record<number, FaerbungReviewScore>),
      allIds: fs.map((s) => s.id),
    },
  };
}

function addUpdateFaerbungReviewScore(
  state: FaerbungReviewState, fs: FaerbungReviewScore,
): FaerbungReviewState {
  const byId = { ...state.faerbungReviewScore.byId, [fs.id]: fs };
  const allIds = Object.keys(byId).map((k) => Number(k));
  return {
    ...state,
    faerbungReviewScore: {
      ...state.faerbungReviewScore,
      byId,
      allIds,
    },
  };
}

function FaerbungReviewReducer(
  state = initialState, action: FaerbungReivewActionTypes,
): FaerbungReviewState {
  switch (action.type) {
    case SET_FAERBUNG_REVIEW_METAS:
      return setFaerbungReviewMetas(state, action.payload);
    case ADD_UPDATE_FAERBUNG_REVIEW_META:
      return addUpdateFaerbungReviewMetas(state, action.payload);
    case DELETE_FAERBUNG_REVIEW_META:
      return deleteFaerbungReviewMeta(state, action.payload);
    case RESET_FAERBUNG_REVIEW_META:
      return {
        ...initialState,
        faerbungReviewMeta: {
          allIds: undefined,
          byId: undefined,
        },
      };
    case SET_FAERBUNG_REVIEW_SCORE:
      return setFaerbungReviewScores(state, action.payload);
    case ADD_UPDATE_FAERBUNG_REVIEW_SCORE:
      return addUpdateFaerbungReviewScore(state, action.payload);
    default:
      return state;
  }
}

export default FaerbungReviewReducer;

/** -------------------Selectors------ */

const selectFaerbungReviewMetaAllId = (state: RootState) => state
  .faerbungReview.faerbungReviewMeta.allIds;
const selectFaerbungReviewMetaByIds = (state: RootState) => state
  .faerbungReview.faerbungReviewMeta.byId;
const selectFaerbungReviewScoresAllId = (state: RootState) => state
  .faerbungReview.faerbungReviewScore.allIds;
const selectFaerbungReviewScoresById = (state: RootState) => state
  .faerbungReview.faerbungReviewScore.byId;

export const selectFaerbungReviewMeta = createSelector([
  selectFaerbungReviewMetaAllId,
  selectFaerbungReviewMetaByIds,
], (allIds, byId) => {
  if (allIds && byId) {
    return allIds.map((id) => byId[id]);
  }
  return undefined;
});

const selectedSection = (state: RootState, sectionId: number | undefined) => sectionId;
export const selectSelectedFaerbungReviewMeta = createSelector([
  selectFaerbungReviewMetaAllId,
  selectFaerbungReviewMetaByIds,
  selectedSection,
], (allIds, byId, sectionId) => {
  if (allIds && byId) {
    const metas = allIds.map((id) => byId[id]);
    return metas.find((m) => m.digitalSectionId === sectionId);
  }
  return undefined;
});

export const selectFaerbungReviewScore = createSelector([
  selectFaerbungReviewScoresAllId,
  selectFaerbungReviewScoresById,
], (allIds, byId) => allIds.map((id) => byId[id]));

export const selectFaerbungReviewScoreByCaseId = createSelector([
  selectFaerbungReviewScore,
], (scoreList) => {
  const scoreMapByCaseId = scoreList.reduce((acc, cur) => {
    acc[cur.digitalCaseId] = cur;
    return acc;
  }, {} as Record<number, FaerbungReviewScore>);
  return scoreMapByCaseId;
});

export const selectFaerbungReviewScoreBySectionId = createSelector([
  selectFaerbungReviewScore,
  selectedSection,
], (scoreList, sectionId) => {
  const scoreBySection = scoreList
    .filter((s) => s.digitalSectionId === sectionId)
    .reduce((acc, cur) => acc + cur.score, 0);
  return scoreBySection;
});

export const selectTotalFaerbungReviewScore = createSelector([
  selectFaerbungReviewScore,
], (scoreList) => scoreList.reduce((acc, cur) => acc + cur.score, 0));

export const selectFaerbungOverallPassScore = createSelector([
  selectFaerbungReviewMeta,
], (metaList) => metaList?.reduce((acc, cur) => acc + cur.passingScore, 0));
